import { useCallback, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "primereact/button"
import { InputText } from "primereact/inputtext"
import { Password } from "primereact/password"
import { useAuth } from "../../context/authContext"
import { ValidateEmail } from "../../helpers/emailHelpers"
import { setSessStorage } from "../../helpers/sessionStorage"
import LoaderFullPage from "../../components/Loaders"
import logo from "../../assets/images/logo-white-text.png"
import AuthMessages from "../Login/components/AuthMessages"
import authStyles from "../../styles/Authentication.module.css"
import useAuthActionLink from "./hooks/useAuthActionLink"

export default function PasswordReset() {
  const { auth, sendPasswordResetEmail } = useAuth()
  const navigate = useNavigate()

  // Keep a stable verifier callback so the action-link hook does not
  // restart verification on every render.
  const verifyResetCode = useCallback(
    (code) => {
      return auth.verifyPasswordResetCode(code)
    },
    [auth],
  )

  const [loading, setLoading] = useState(false)
  const [newPassword, setNewPassword] = useState("")
  const [recoveryEmail, setRecoveryEmail] = useState("")
  const [hasRequestedNewLink, setHasRequestedNewLink] = useState(false)
  // Once reset succeeds, keep only the success confirmation visible.
  const [hasConfirmedPasswordReset, setHasConfirmedPasswordReset] =
    useState(false)
  const [err, setErr] = useState(null)
  const [msg, setMsg] = useState(null)

  // Shared hook keeps Firebase action-link parsing and verification consistent
  // with future auth flows (2FA removal, email verification, etc.).
  const {
    oobCode,
    isVerifying,
    isVerified,
    verifiedPayload: resetEmail,
    verificationError,
  } = useAuthActionLink({
    expectedMode: "resetPassword",
    verifyCode: verifyResetCode,
    invalidLinkMessage: "This password reset link is invalid or expired.",
  })

  const displayError = err || (hasRequestedNewLink ? null : verificationError)
  const resetEmailDisplay = resetEmail || "Unknown e-mail"
  const canRenderResetForm = isVerified && !hasConfirmedPasswordReset
  const canRenderRecoveryForm =
    !isVerifying &&
    !isVerified &&
    !hasRequestedNewLink &&
    !hasConfirmedPasswordReset

  if (isVerifying) return <LoaderFullPage />

  const handleSubmit = async (event) => {
    event.preventDefault()
    // Prevent double-submit and block submit while link validation is still pending.
    if (loading || isVerifying || !isVerified) return

    setErr(null)
    setMsg(null)

    // Keep validation aligned with Firebase minimum password requirements.
    if (!newPassword || newPassword.length < 6) {
      setErr("Password must be at least 6 characters.")
      return
    }

    setLoading(true)
    try {
      await auth.confirmPasswordReset(oobCode, newPassword)
      // Clear the input after success to avoid leaving sensitive text on-screen.
      setMsg("Password updated. You can now sign in with your new password.")
      setNewPassword("")
      setHasConfirmedPasswordReset(true)
    } catch (error) {
      const errorCode = error?.code || ""

      if (
        errorCode === "auth/expired-action-code" ||
        errorCode === "auth/invalid-action-code"
      ) {
        const linkErrorMessage =
          "This password reset link is invalid or expired. Please request a new link."
        setSessStorage("auth_action_error", linkErrorMessage)
        navigate("/", {
          replace: true,
          state: { prefillEmail: resetEmail || recoveryEmail },
        })
        return
      } else {
        setErr("Unable to reset password right now. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReRequestEmail = async (event) => {
    event.preventDefault()
    if (loading || !canRenderRecoveryForm) return

    setErr(null)
    setMsg(null)

    const emailValidation = ValidateEmail(recoveryEmail)
    if (!emailValidation.isValid) {
      setErr(emailValidation.message)
      return
    }

    setLoading(true)
    try {
      await sendPasswordResetEmail(recoveryEmail.trim())
      setMsg(
        "If your email is registered, a password reset link has been sent. Check your inbox and spam folder.",
      )
      setHasRequestedNewLink(true)
    } catch (error) {
      setErr("Unable to send reset email right now. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="layout-login">
      <img src={logo} alt="Recovery Link Logo" className="main-logo" />
      <div className="login-container">
        <h2>Reset your password</h2>
        {canRenderResetForm && (
          <>
            <form onSubmit={handleSubmit} className="form-section">
              <div className="form-field">
                <p className={`${authStyles.subhead} ${authStyles.resetEmailDisplay}`}>
                  <span className={authStyles.resetEmailText}>{resetEmailDisplay}</span>
                </p>
                <label htmlFor="new-password">New password</label>
                <Password
                  inputId="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  feedback={false}
                  toggleMask
                  className={authStyles.passwordFieldWrapper}
                  inputClassName={authStyles.passwordFieldInput}
                  disabled={loading || isVerifying || !isVerified}
                />
              </div>
              <Button
                label={loading ? "Saving..." : "Save"}
                type="submit"
                className={`btn ${authStyles.actionBtn} ${authStyles.authActionButton}`}
                loading={loading}
                disabled={loading || isVerifying || !isVerified || !newPassword}
              />
            </form>
          </>
        )}
        {canRenderRecoveryForm && (
          <form onSubmit={handleReRequestEmail} className="form-section">
            <p className={authStyles.subhead}>Need a new reset link?</p>
            <div className="form-field">
              <label htmlFor="recovery-email">Email</label>
              <InputText
                id="recovery-email"
                type="email"
                value={recoveryEmail}
                onChange={(event) => setRecoveryEmail(event.target.value)}
                className={authStyles.fullWidthControl}
                disabled={loading}
              />
            </div>
            <Button
              label={loading ? "Sending..." : "Request new reset e-mail"}
              type="submit"
              className={`btn ${authStyles.actionBtn} ${authStyles.authActionButton}`}
              loading={loading}
              disabled={loading || !recoveryEmail}
            />
          </form>
        )}
        <AuthMessages err={displayError} msg={msg} logoutMsg={null} />
        <button
          className={`${authStyles.plainTextLink} ${authStyles.backLink}`}
          type="button"
          onClick={() => {
            navigate("/", { replace: true })
          }}
          disabled={loading}
        >
          Back to Login
        </button>
      </div>
    </div>
  )
}
